import * as React from 'react'

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { CreditCard, Languages, LogOut, MoreVertical, Palette } from 'lucide-react'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Link, useNavigate } from 'react-router-dom'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar'

import { DialogTrigger } from '@/components/ui/dialog'
// Remove incorrect type import: import type { UseSessionResult } from "better-auth/react";
import { authClient } from '@/lib/auth-client' // Keep for signOut and type inference
import { useState } from 'react'
import { useTheme } from '@/components/theme-provider'
import { useTranslation } from 'react-i18next'

// Use Lucide icons

interface NavUserProps {
	sessionResult: ReturnType<typeof authClient.useSession> // Use inferred type
}

export function NavUser({ sessionResult }: NavUserProps) {
	// Use props
	const { isMobile } = useSidebar()
	const navigate = useNavigate()
	const { t, i18n } = useTranslation()
	const { setTheme } = useTheme()
	// const { data: session, isPending } = authClient.useSession(); // Remove internal session fetching
	const { data: session, isPending } = sessionResult // Use prop data
	const [isAlertOpen, setAlertOpen] = useState(false)

	const handleLogout = async () => {
		// Keep authClient for signOut
		await authClient.signOut({
			fetchOptions: {
				onSuccess: () => {
					navigate('/login')
				},
			},
		})
	}

	const changeLanguage = (lng: string) => {
		i18n.changeLanguage(lng)
	}

	if (isPending) {
		return (
			<SidebarMenu>
				<SidebarMenuItem>
					<SidebarMenuButton size='lg' className='animate-pulse'>
						<div className='h-8 w-8 rounded-lg bg-muted' />
						<div className='flex-1 space-y-1 py-1'>
							<div className='h-3 w-20 rounded bg-muted' />
							<div className='h-2 w-24 rounded bg-muted' />
						</div>
					</SidebarMenuButton>
				</SidebarMenuItem>
			</SidebarMenu>
		)
	}

	if (!session) {
		return null
	}

	const user = session.user

	return (
		<>
			<AlertDialog open={isAlertOpen} onOpenChange={setAlertOpen}>
				<SidebarMenu>
					<SidebarMenuItem>
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<SidebarMenuButton
									size='lg'
									className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
								>
									<Avatar className='h-8 w-8 rounded-lg'>
										<AvatarImage src={user.image ?? undefined} alt={user.name ?? ''} />
										<AvatarFallback className='rounded-lg'>
											{user.name?.charAt(0).toUpperCase() ?? 'U'}
										</AvatarFallback>
									</Avatar>
									<div className='grid flex-1 text-left text-sm leading-tight'>
										<span className='truncate font-semibold'>{user.name}</span>

										<span className='text-muted-foreground truncate text-xs'>{user.email}</span>
									</div>
									<MoreVertical className='ml-auto size-4' />
								</SidebarMenuButton>
							</DropdownMenuTrigger>
							<DropdownMenuContent
								className='min-w-56 rounded-lg p-2'
								side={isMobile ? 'bottom' : 'right'}
								align='end'
								sideOffset={4}
							>
								<DropdownMenuLabel className='p-0 font-normal'>
									<div className='flex items-center gap-2 px-1 py-1 text-left text-sm'>
										<Avatar className='h-8 w-8 rounded-lg'>
											<AvatarImage src={user.image ?? undefined} alt={user.name ?? ''} />
											<AvatarFallback className='rounded-lg'>
												{user.name?.charAt(0).toUpperCase() ?? 'U'}
											</AvatarFallback>
										</Avatar>
										<div className='grid flex-1 text-left text-sm leading-tight'>
											<span className='truncate font-semibold'>{user.name}</span>

											<span className='text-muted-foreground truncate text-xs'>{user.email}</span>
										</div>
									</div>
								</DropdownMenuLabel>
								<DropdownMenuSeparator className='my-2' />
								<DropdownMenuGroup>
									<DropdownMenuItem className='px-2 py-1' onClick={() => navigate('/billing')}>
										<CreditCard className='mr-2 h-4 w-4' />
										<span>{t('sidebar.user.billing')}</span>
									</DropdownMenuItem>
								</DropdownMenuGroup>
								<DropdownMenuSeparator className='my-2' />
								<DropdownMenuGroup>
									<DropdownMenuSub>
										<DropdownMenuSubTrigger className='px-2 py-1'>
											<Palette className='mr-2 h-4 w-4' />
											<span>{t('sidebar.user.theme')}</span>
										</DropdownMenuSubTrigger>
										<DropdownMenuPortal>
											<DropdownMenuSubContent className='p-1'>
												<DropdownMenuItem
													className='px-2 py-1'
													onClick={() => setTheme('light')}
												>
													{t('sidebar.user.theme.light')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className='px-2 py-1'
													onClick={() => setTheme('dark')}
												>
													{t('sidebar.user.theme.dark')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className='px-2 py-1'
													onClick={() => setTheme('system')}
												>
													{t('sidebar.user.theme.system')}
												</DropdownMenuItem>
											</DropdownMenuSubContent>
										</DropdownMenuPortal>
									</DropdownMenuSub>
									<DropdownMenuSub>
										<DropdownMenuSubTrigger className='px-2 py-1'>
											<Languages className='mr-2 h-4 w-4' />
											<span>{t('sidebar.user.language')}</span>
										</DropdownMenuSubTrigger>
										<DropdownMenuPortal>
											<DropdownMenuSubContent className='p-1'>
												<DropdownMenuItem
													className='px-2 py-1'
													onClick={() => changeLanguage('en')}
													disabled={i18n.language === 'en'}
												>
													{t('sidebar.user.language.en')}
												</DropdownMenuItem>
												<DropdownMenuItem
													className='px-2 py-1'
													onClick={() => changeLanguage('zh')}
													disabled={i18n.language === 'zh'}
												>
													{t('sidebar.user.language.zh')}
												</DropdownMenuItem>
											</DropdownMenuSubContent>
										</DropdownMenuPortal>
									</DropdownMenuSub>
								</DropdownMenuGroup>
								<DropdownMenuSeparator className='my-2' />
								<AlertDialogTrigger asChild>
									<DropdownMenuItem
										className='px-2 py-1'
										onSelect={(event) => event.preventDefault()}
									>
										<LogOut className='mr-2 h-4 w-4' />
										<span>{t('sidebar.user.logout')}</span>
									</DropdownMenuItem>
								</AlertDialogTrigger>
							</DropdownMenuContent>
						</DropdownMenu>
					</SidebarMenuItem>
				</SidebarMenu>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle className='text-lg font-semibold'>
							{t('sidebar.user.logoutConfirmTitle')}
						</AlertDialogTitle>

						<AlertDialogDescription>{t('sidebar.user.logoutConfirmDescription')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('sidebar.user.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleLogout}>{t('sidebar.user.confirmLogout')}</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
